// AA — Cart upsell strip behavior.
//
// Lives in a static asset (loaded unconditionally from the cart drawer in
// header-actions.liquid) instead of an inline <script> in the shell snippet:
// the shell only renders once the cart has items, so on a page loaded with an
// empty cart the markup arrives later via the drawer morph — and scripts
// injected that way never execute. The custom element definition must already
// be registered so the morphed-in <aa-cart-upsell> upgrades and hydrates.
import { StandardEvents } from '@shopify/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';

if (!customElements.get('aa-cart-upsell')) {
  customElements.define(
    'aa-cart-upsell',
    class extends HTMLElement {
      #refreshTimer;
      #html = '';
      #loadId = 0;

      // CartLinesUpdateEvent fires *before* the cart request is sent; its
      // promise settles once the cart has actually changed. Reading the cart
      // on a fixed delay raced the request, so a slow add left the strip
      // built from the old cart (offering the product just added). Wait for
      // the promise, then refresh. The short debounce coalesces bursts (e.g.
      // quantity +/- spam) into one fetch.
      #onCartUpdate = (event) => {
        clearTimeout(this.#refreshTimer);
        const refresh = () => {
          clearTimeout(this.#refreshTimer);
          this.#refreshTimer = setTimeout(() => this.#load(), 150);
        };
        if (event?.promise) {
          event.promise.then(refresh, refresh);
        } else {
          this.#refreshTimer = setTimeout(() => this.#load(), 900);
        }
      };

      connectedCallback() {
        document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
        // Scroll doesn't bubble but does capture, so one listener on the
        // component survives the track being replaced by hydration or the
        // drawer morph — no rebinding to chase.
        this.addEventListener('scroll', this.#updateScrollbar, { capture: true, passive: true });
        new ResizeObserver(this.#updateScrollbar).observe(this);
        // Desktop affordances: a mouse has no horizontal gesture, so
        // translate vertical wheel over the strip, drag-to-scroll on the
        // cards, and let the indicator bar act as a slider.
        this.addEventListener('wheel', this.#onWheel, { passive: false });
        this.addEventListener('pointerdown', this.#onPointerDown);
        this.addEventListener('dragstart', this.#onDragStart);
        // The theme re-renders the header section (which holds the drawer)
        // shortly after load and after cart changes, and morphs it back to the
        // server markup: `hidden` on, cards gone, same element so no
        // reconnect. Re-apply the last hydrated cards whenever that happens.
        new MutationObserver(this.#restore).observe(this, {
          attributes: true,
          attributeFilter: ['hidden'],
          childList: true,
          subtree: true,
        });
        this.#load();
      }

      disconnectedCallback() {
        document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
        clearTimeout(this.#refreshTimer);
      }

      async #load() {
        // Overlapping loads (cart events in quick succession) can resolve out
        // of order; only the newest one may write.
        const loadId = ++this.#loadId;
        try {
          const cart = await (await fetch('/cart.js', { cache: 'no-store' })).json();
          if (loadId !== this.#loadId) return;
          if (!cart.items?.length) {
            this.#html = '';
            this.hidden = true;
            return;
          }
          // A cap already in the cart: offer what they looked at instead.
          let html = this.#withoutCartProducts(await this.#fetchRecentlyViewed(cart), cart);
          // A thin strip (one or two recently viewed, few complementary picks)
          // is topped up with Search & Discovery picks: complementary, then related.
          const min = Number(this.dataset.min) || 3;
          if (this.#cardCount(html) < min) {
            const pid = cart.items[0].product_id;
            const intent = this.dataset.source === 'related' ? 'related' : 'complementary';
            html = this.#merge(html, this.#withoutCartProducts(await this.#fetchItems(pid, intent), cart));
            if (this.#cardCount(html) < min && intent === 'complementary') {
              html = this.#merge(html, this.#withoutCartProducts(await this.#fetchItems(pid, 'related'), cart));
            }
          }
          if (loadId !== this.#loadId) return;
          if (!this.#cardCount(html)) {
            this.#html = '';
            this.hidden = true;
            return;
          }
          this.#html = html;
          this.querySelector('[data-items]').innerHTML = html;
          this.#restore();
        } catch (e) {
          // Upsell failures must never break the cart.
          console.warn('aa-cart-upsell:', e);
        }
      }

      async #fetchItems(productId, intent) {
        return this.#fetchOffer(
          `${this.dataset.url}?section_id=aa-cart-upsell-items&product_id=${productId}&limit=12&intent=${intent}`
        );
      }

      // Recently viewed ids live in localStorage, so the search route renders
      // them (same query the predictive search uses for its recently viewed).
      async #fetchRecentlyViewed(cart) {
        if (!cart.items.some((item) => item.product_type === 'Cap')) return '';
        const inCart = new Set(cart.items.map((item) => String(item.product_id)));
        const ids = RecentlyViewed.getProducts().filter((id) => !inCart.has(String(id)));
        if (!ids.length) return '';
        const url = new URL(Theme.routes.search_url, location.origin);
        url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
        url.searchParams.set('type', 'product');
        url.searchParams.set('section_id', 'aa-cart-upsell-items');
        const html = await this.#fetchOffer(url);
        if (!html) return '';
        // Search answers in relevance order; this list reads newest first.
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const track = doc.querySelector('.aa-cart-upsell__track');
        ids.forEach((id) => {
          const card = track.querySelector(`product-card[data-product-id="${id}"]`)?.closest('.aa-cart-upsell__card');
          if (card) track.append(card);
        });
        return doc.querySelector('[data-offer]').outerHTML;
      }

      // Appends the cards of `extra` that `html` doesn't show yet, up to the
      // strip's max. Once any are added the strip is mixed, so it takes the
      // heading of `extra` (a topped-up strip is no longer only recently viewed).
      #merge(html, extra) {
        if (!this.#cardCount(html)) return extra;
        if (!this.#cardCount(extra)) return html;
        const offer = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-offer]');
        const more = new DOMParser().parseFromString(extra, 'text/html').querySelector('[data-offer]');
        const track = offer.querySelector('.aa-cart-upsell__track');
        const max = Number(this.dataset.max) || 8;
        const seen = new Set([...track.querySelectorAll('product-card')].map((card) => card.dataset.productId));
        let added = 0;
        more.querySelectorAll('.aa-cart-upsell__card').forEach((card) => {
          const id = card.querySelector('product-card')?.dataset.productId;
          if (!id || seen.has(id) || track.children.length >= max) return;
          seen.add(id);
          track.append(card);
          added++;
        });
        if (added) {
          offer.querySelector('.aa-cart-upsell__heading').replaceWith(more.querySelector('.aa-cart-upsell__heading'));
        }
        return offer.outerHTML;
      }

      async #fetchOffer(url) {
        // The cards depend on the cart cookie, so never reuse a cached copy.
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) return '';
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        return doc.querySelector('[data-offer]')?.outerHTML ?? '';
      }

      // The items section already skips cart products server-side; this is
      // the client-side backstop in case that render saw a stale cart.
      #withoutCartProducts(html, cart) {
        if (!html) return html;
        const inCart = new Set(cart.items.map((item) => String(item.product_id)));
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const track = doc.querySelector('.aa-cart-upsell__track');
        if (!track) return html;
        track.querySelectorAll('.aa-cart-upsell__card').forEach((card) => {
          const id = card.querySelector('[data-product-id]')?.dataset.productId;
          if (id && inCart.has(id)) card.remove();
        });
        return doc.querySelector('[data-offer]').outerHTML;
      }

      // No-op once the cards are in place, so its own writes don't loop.
      #restore = () => {
        const items = this.querySelector('[data-items]');
        if (!this.#html || !items) return;
        if (!items.querySelector('.aa-cart-upsell__track')) items.innerHTML = this.#html;
        if (this.hidden) this.hidden = false;
        this.#updateScrollbar();
      };

      #cardCount(html) {
        return (html.match(/aa-cart-upsell__card-media/g) || []).length;
      }

      #track() {
        const track = this.querySelector('.aa-cart-upsell__track');
        if (!track) return null;
        return track.scrollWidth - track.clientWidth >= 2 ? track : null;
      }

      // Vertical wheel over the strip moves it horizontally; horizontal
      // wheel (trackpads) already works natively, so leave it alone.
      #onWheel = (e) => {
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        if (!e.target.closest('.aa-cart-upsell__track')) return;
        const track = this.#track();
        if (!track) return;
        track.scrollLeft += e.deltaY;
        e.preventDefault();
      };

      // Native image drag hijacks drag-to-scroll on the cards.
      #onDragStart = (e) => {
        if (e.target.closest('.aa-cart-upsell__track')) e.preventDefault();
      };

      #onPointerDown = (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        const track = this.#track();
        if (!track) return;

        const bar = e.target.closest('.aa-cart-upsell__scrollbar');
        if (bar) {
          // The indicator doubles as a slider: click seeks, drag scrubs.
          const rect = bar.getBoundingClientRect();
          const overflow = track.scrollWidth - track.clientWidth;
          const seek = (x) => {
            const frac = Math.min(Math.max((x - rect.left) / rect.width, 0), 1);
            track.scrollLeft = frac * overflow;
          };
          seek(e.clientX);
          const move = (ev) => seek(ev.clientX);
          const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
          e.preventDefault();
          return;
        }

        if (!e.target.closest('.aa-cart-upsell__track')) return;
        const startX = e.clientX;
        const startLeft = track.scrollLeft;
        let dragged = false;
        const move = (ev) => {
          const dx = ev.clientX - startX;
          if (!dragged && Math.abs(dx) > 4) dragged = true;
          if (dragged) track.scrollLeft = startLeft - dx;
        };
        const up = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          if (dragged) {
            // Swallow the click that follows a drag so cards don't open.
            const suppress = (ce) => {
              ce.preventDefault();
              ce.stopPropagation();
            };
            this.addEventListener('click', suppress, { capture: true, once: true });
            setTimeout(() => this.removeEventListener('click', suppress, { capture: true }), 150);
          }
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      };

      // Resolves the track fresh on every call, so it works no matter
      // how many times hydration or the drawer morph replaces the DOM.
      #updateScrollbar = () => {
        const track = this.querySelector('.aa-cart-upsell__track');
        const bar = this.querySelector('[data-scrollbar]');
        const thumb = this.querySelector('[data-scrollbar-thumb]');
        if (!track || !bar || !thumb) return;
        const overflow = track.scrollWidth - track.clientWidth;
        // Only write on change: re-setting `hidden` still queues a mutation,
        // which would re-enter #restore through the observer.
        if (overflow < 2) {
          if (!bar.hidden) bar.hidden = true;
          return;
        }
        if (bar.hidden) bar.hidden = false;
        // Half the true proportion, clamped, so the thumb reads as a
        // short marker rather than filling most of the bar.
        const ratio = Math.min(Math.max(track.clientWidth / track.scrollWidth * 0.5, 0.1), 0.3);
        thumb.style.width = `${ratio * 100}%`;
        const progress = track.scrollLeft / overflow;
        thumb.style.insetInlineStart = `${progress * (1 - ratio) * 100}%`;
      };
    }
  );
}
